import React, { useMemo } from 'react';
import { TileToolbar } from 'widgets/tiles/components/tile/TileToolbar';
import { SymbolFromConfigOrPortfolioLinkedStatus } from 'portfolio/components/SymbolFromConfigOrPortfolioLinkedStatus';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { WidgetTile } from 'widgets/tiles/components/tile/WidgetTile';
import { useCandlestickTileConfigSlice } from 'widgets/candlestickTile/store/config/configslice';
import { CandlestickTileContent } from 'widgets/candlestickTile/components/CandlestickTileContent';
import CandlestickTileSettings, {
    CandlestickTileSettingsText,
} from 'widgets/candlestickTile/components/settings/CandlestickTileSettings';
import CandlestickTileChartHelp from 'widgets/candlestickTile/components/chart/CandlestickTileChartHelp';
import { buildCandlestickTileData } from 'widgets/candlestickTile/candlestickTileData';
import classNames from 'classnames';
import ConfigSettingsModal from 'widgets/tiles/components/tile/ConfigSettingsModal';

import { useWidgetContext } from 'widgets/WidgetContext';
import { priceStreamedDataSelectors } from 'widgets/candlestickTile/store/stream/streamSlice';
import { useCapsuleBoundSymbol } from 'widgets/tiles/components/tile/hooks';
import {
    useExternalMarketBarData,
    useExternalMarketBarDataMessages,
} from 'marketData/hooks';
import { StreamMessage } from 'utils/messages/StreamMessage';

const { useWidgetMessages, useWidgetStreamData } = priceStreamedDataSelectors;

export function CandlestickTile() {
    const { config, dispatchConfigChanged } = useCandlestickTileConfigSlice();
    const { bucketSize, showDataFor } = config;

    const { symbol, symbology } = useCapsuleBoundSymbol();
    const streamData = useWidgetStreamData();
    const externalMarketData = useExternalMarketBarData(
        symbol,
        symbology,
        config.bucketSize
    );
    const widgetMessages = useWidgetMessages();
    const externalMarketDataMessages = useExternalMarketBarDataMessages(
        symbol,
        symbology,
        config.bucketSize
    );

    const messages: StreamMessage[] = [
        ...widgetMessages,
        ...externalMarketDataMessages,
    ];
    const data = buildCandlestickTileData(
        bucketSize,
        streamData,
        externalMarketData,
        showDataFor
    );

    const { widgetKey } = useWidgetContext();

    useWidgetStream();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const warning = useMemo(() => <SymbolWarning />, [config]);

    const cssClasses = classNames('CandlestickTile');

    return (
        <WidgetTile className={cssClasses}>
            <TileToolbar
                title="Candlestick"
                settingsBody={<CandlestickTileSettings />}
                settingsHeader={CandlestickTileSettingsText}
                preceedingItems={<CandlestickTileChartHelp />}
                messages={messages}
            />

            <div className="capsule-body">
                <SymbolFromConfigOrPortfolioLinkedStatus warning={warning}>
                    <CandlestickTileContent
                        data={data}
                        config={config}
                        dispatchConfigChanged={dispatchConfigChanged}
                        symbol={symbol}
                        capsuleKey={widgetKey}
                    />
                </SymbolFromConfigOrPortfolioLinkedStatus>
            </div>
        </WidgetTile>
    );

    function SymbolWarning() {
        return (
            <span>
                Select a symbol or specify one in the&nbsp;
                <ConfigSettingsModal
                    dispatcher={dispatchConfigChanged}
                    headerText={CandlestickTileSettingsText}
                    triggerText={'Capsule Settings'}
                    renderSettings={(p) => <CandlestickTileSettings {...p} />}
                />
                .
            </span>
        );
    }
}
